import { ArrowDownTrayIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Sortable } from 'components/Sortable'
import {
  channelTypeOptions,
  loanOptionsConstants,
  loanStatusConstants,
  lockTypeOptions,
  productTypeOptions,
} from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import {
  downloadReports,
  downloadReportsCSV,
  generateReportsPreview,
  getAdminConfig,
  getLoanReport,
  getLoanReportsPlaceholders,
  getParties,
  saveReports,
} from 'services'
import { svgLoading, svgSearch } from 'stories/assets'
import { Button, HorzScrollContainer, Input2, InputDateRange, Modal, MultiSelect } from 'stories/components'
import { Tooltip } from 'stories/components/Tooltip/Tooltip'
import { filterObject, generateOptions } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { partyHeaders } from './constant'
import { ReportPlaceholders } from './ReportPlaceholders'
import { IReportHeaders, LoanReportsOrigin } from './types'

const RAW_DATE_HEADERS = [
  'Notes Date',
  'Appraisal\nDate',
  'Borrow Application completed date',
  'Appraisal Date',
  'ClosingDate',
  'Funded Date',
  'Submitted Date',
  'Purchase Date',
  'First Payment Date',
  'Maturity Date',
  'Permits Expiration Date',
  'TL Date',
  'Appraisal Review Date',
  'AP Accepted Date',
  'AP Complete Date',
  'AP Inspection Date',
  'AP Lender Due Date',
  'AP Order Date',
  'AP Paid Date',
  'AP Received Date by Lender',
  'PAB Purchased Date',
  'PAB Purchased Date - Sweep',
  'PAS Purchased Date',
  'PAS Purchased Date - Sweep',
  'Requested Wire Date',
  'Additional Real Estate Tax Due Date',
  'Current Maturity Date',
  'Date First Insurance Information Required Notice Sent',
  'Date of Determination',
  'Insurance Cancellation Date',
  'Insurance Policy #1 Expiration Date',
  'Missed Payment Date',
  'NFIP Community Date',
  'Original Maturity Date',
  'Origination Date',
  'Payoff Amount Through Date',
  'Real Estate Tax Due Date',
  'Tax Due Notice Sent Date',
]

const DATE_HEADERS = Array.from(new Set(RAW_DATE_HEADERS.map((header) => header.trim())))
const DATE_HEADERS_SET = new Set(DATE_HEADERS)

type ActionType = 'preview' | 'download' | 'downloadCSV'

type DateFilterPayload = {
  column: string
  range: {
    from?: string
    to?: string
  }
}

const extractHeaderLabel = (header: string) => {
  const trimmed = header.trim()
  if (trimmed.includes(' - ')) return trimmed.split(' - ').pop()!.trim()
  if (trimmed.includes('-')) return trimmed.split('-').pop()!.trim()
  return trimmed
}

const ReportDetails = () => {
  const { id } = useParams() as any
  const navigate = useHistory()
  const location = useLocation()
  const queryOrigin = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const value = params.get('origin')
    if (value === null) return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }, [location.search])

  const isNew = Number(id) == 0
  const isStandard = Number(id) != id
  const [loading, setLoading] = useState(false)
  const [placeholders, setPlaceholders] = useState<Record<string, IReportHeaders[]>>({ Loan: [] })
  const [type, setType] = useState('Loan')
  const [selectedPlaces, setSelectedPlaces] = useState<string[]>(['Loan #'])
  const [title, setTitle] = useState('')

  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])

  const fullLoanStatusOptions = { ...loanGlobalStatus, ...loanStatusConstants.servicingStatus }
  const [origin, setOrigin] = useState<LoanReportsOrigin>(
    (queryOrigin as LoanReportsOrigin) ?? LoanReportsOrigin.Origination,
  )

  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    status: generateOptions(fullLoanStatusOptions),
    lock: generateOptions(lockTypeOptions),
    state: generateOptions(loanOptionsConstants.states),
    type: generateOptions(channelTypeOptions), // Channel
    productType: generateOptions(productTypeOptions), // Product Type
    asOf: {},
    orderBy: 'asOf',
    orderDir: '-1',
    skip: 0,
    count: 20,
  })
  const [preview, setPreview] = useState<string[][]>([])
  const [total, setTotal] = useState(0)
  const [queryTimes, setQueryTimes] = useState(0)
  const [partyTemp, setPartyTemp] = useState<Record<string, any>>({})
  const [isDateModalOpen, setIsDateModalOpen] = useState(false)
  const [dateModalColumns, setDateModalColumns] = useState<string[]>([])
  const [dateFilterColumn, setDateFilterColumn] = useState('')
  const [dateFilterRange, setDateFilterRange] = useState<{ from?: string; to?: string }>({})
  const [pendingAction, setPendingAction] = useState<ActionType | null>(null)
  const [dateModalError, setDateModalError] = useState('')

  useEffect(() => {
    initData()
    getParties(0).then((data) => {
      const rtl: Record<string, any> = {}
      data.map((item: Record<string, any>) => {
        rtl[item.Type] = item.CustomName
      })
      setPartyTemp(rtl)
    })
  }, [])

  const initData = async () => {
    setLoading(true)
    const data = await getLoanReportsPlaceholders()

    !isStandard && setPlaceholders(data)
    if (isNew || isStandard) {
      if (id == 'hmda') {
        const { config } = await getAdminConfig('hmda')
        const newData = {
          Loan: [
            data.Loan[0],
            {
              title: 'HMDA',
              data: Object.keys(config),
            },
          ],
        }
        setPlaceholders(newData)
      }
      setLoading(false)
      return
    }

    getLoanReport(id)
      .then(({ title, headers, filters, origin: reportOrigin }) => {
        setTitle(title)
        setSelectedPlaces(headers)
        setFilters(filters)
        if (reportOrigin !== undefined) {
          setOrigin(reportOrigin)
        }
      })
      .finally(() => setLoading(false))
  }

  const onChangeFilter = (key: string, value: any) => {
    const newFilters = cloneDeep(filters)
    newFilters[key] = value
    setFilters(newFilters)
  }

  const onSave = () => {
    setLoading(true)
    saveReports(id, { type, title, headers: selectedPlaces, filters, origin })
      .then(() => navigate.push('/reports'))
      .finally(() => setLoading(false))
  }

  const makeEmptyIfAll = (options: Record<string, boolean>, fullOptions: string[] | Record<string, any>) => {
    const arrOptions = filterObject(options)
    const isSelectedAll = arrOptions.length == Object.keys(generateOptions(fullOptions)).length
    return isSelectedAll ? [] : arrOptions
  }

  const getFilters = () => {
    return {
      ...filters,
      status: makeEmptyIfAll(filters.status, fullLoanStatusOptions),
      lock: makeEmptyIfAll(filters.lock, lockTypeOptions),
      type: makeEmptyIfAll(filters.type, channelTypeOptions),
      productType: makeEmptyIfAll(filters.productType, productTypeOptions),
      state: makeEmptyIfAll(filters.state, loanOptionsConstants.states),
    }
  }

  const getSelectedDateColumns = () => {
    const uniqueHeaders = new Set<string>()
    selectedPlaces.forEach((header) => {
      const label = extractHeaderLabel(header)
      if (DATE_HEADERS_SET.has(label)) uniqueHeaders.add(header)
    })
    return Array.from(uniqueHeaders)
  }

  const buildFiltersWithDateRange = (dateFilter?: DateFilterPayload) => {
    const computedFilters: Record<string, any> = { ...getFilters() }
    const hasFrom = !!dateFilter?.range.from
    const hasTo = !!dateFilter?.range.to
    if (dateFilter && dateFilter.column && (hasFrom || hasTo)) {
      computedFilters.dateRangeFilter = {
        column: dateFilter.column,
        from: dateFilter.range.from,
        to: dateFilter.range.to,
      }
    }
    return computedFilters
  }

  const runAction = (action: ActionType, dateFilter?: DateFilterPayload) => {
    const payloadFilters = buildFiltersWithDateRange(dateFilter)

    if (action === 'preview') {
      setLoading(true)
      generateReportsPreview({
        type,
        id,
        headers: selectedPlaces,
        filters: payloadFilters,
      })
        .then(({ data, total }) => {
          setPreview(data)
          setTotal(total)
          setQueryTimes((prev) => prev + 1)
        })
        .finally(() => setLoading(false))
      return
    }

    if (action === 'download') {
      setLoading(true)
      downloadReports({
        type,
        id,
        title,
        headers: selectedPlaces,
        filters: payloadFilters,
      }).finally(() => setLoading(false))
      return
    }

    if (action === 'downloadCSV') {
      setLoading(true)
      downloadReportsCSV({
        type,
        id,
        title,
        headers: selectedPlaces,
        filters: payloadFilters,
      }).finally(() => setLoading(false))
    }
  }

  const openDateModal = (action: ActionType, columns: string[]) => {
    setPendingAction(action)
    setDateModalColumns(columns)
    const initialColumn = columns.includes(dateFilterColumn) ? dateFilterColumn : columns[0] || ''
    setDateFilterColumn(initialColumn)
    setDateFilterRange({})
    setDateModalError('')
    setIsDateModalOpen(true)
  }

  const handleActionRequest = (action: ActionType) => {
    if (selectedPlaces.length === 0) return
    const matchedColumns = getSelectedDateColumns()
    if (matchedColumns.length === 0) {
      runAction(action)
      return
    }
    openDateModal(action, matchedColumns)
  }

  const onGenerate = () => {
    handleActionRequest('preview')
  }

  const onDownload = () => {
    handleActionRequest('download')
  }

  const onDownloadCSV = () => {
    handleActionRequest('downloadCSV')
  }

  const handleDateModalClose = () => {
    setIsDateModalOpen(false)
    setDateModalError('')
    if (pendingAction) {
      const actionToRun = pendingAction
      setPendingAction(null)
      runAction(actionToRun)
    }
  }

  const handleDateModalOk = () => {
    if (!pendingAction) {
      setIsDateModalOpen(false)
      return true
    }

    if (!dateFilterColumn) {
      setDateModalError('Please select a column.')
      return false
    }

    if (!dateFilterRange.from && !dateFilterRange.to) {
      setDateModalError('Please select at least one date.')
      return false
    }

    const actionToRun = pendingAction
    const dateFilter = {
      column: dateFilterColumn,
      range: { ...dateFilterRange },
    }

    setPendingAction(null)
    setIsDateModalOpen(false)
    setDateModalError('')
    runAction(actionToRun, dateFilter)
    return true
  }

  return (
    <div className="Reports-container py-6 px-2">
      <div className="relative shadow1 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-7 sm:text-center lg:text-left w-full">
        <LayoutLoading show={loading} />
        <div className="relative">
          <div className="text-shade-blue flex items-center mb-2 font-bold">
            <ChevronLeftIcon className="w-4 h-4"></ChevronLeftIcon>
            <Link to={`/reports`} className="hover:underline">
              Return to Reports
            </Link>
          </div>

          <div className="flex flex-wrap justify-between">
            <h1 className="text-2xl font-bold flex items-center mb-2">
              <span>{isNew ? 'Create Report' : title}</span>{' '}
              <span className="text-base ml-3">
                {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />}
              </span>
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ReportPlaceholders
            placeholders={placeholders}
            selectedPlaces={selectedPlaces}
            setSelectedPlaces={setSelectedPlaces}
            type={type}
            setType={setType}
            partyTemp={partyTemp}
          />

          <div className="md:col-span-2">
            {!isStandard && (
              <div className="border-b border-gray-100 pb-4 mb-4">
                <div className="grid items-center grid-cols-2 md:grid-cols-3 gap-4">
                  <Input2 type="text" title="Title" required value={title} onChange={setTitle} />

                  <div>
                    <Button disabled={!title} onClick={onSave}>
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="border-b border-gray-100 pb-4 mb-4">
              <p className="text-lg font-bold mb-2">Filters</p>

              <div className="grid items-center grid-cols-2 md:grid-cols-3 gap-4">
                <Input2
                  type="search"
                  title="Search Loans"
                  hasIcon
                  icon={svgSearch}
                  value={filters.query}
                  onChange={(value) => onChangeFilter('query', value)}
                />
                <MultiSelect
                  id="loan-status"
                  title="Loan Status"
                  options={fullLoanStatusOptions}
                  value={filters.status}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('status', value)}
                />
                <MultiSelect
                  id="loan-type"
                  title="Channel"
                  options={channelTypeOptions}
                  value={filters.type}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('type', value)}
                />
                <MultiSelect
                  id="lock-status"
                  title="Lock"
                  options={lockTypeOptions}
                  value={filters.lock}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('lock', value)}
                />
                <MultiSelect
                  id="product-type"
                  title="Product Type"
                  options={productTypeOptions}
                  value={filters.productType}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('productType', value)}
                />
                <MultiSelect
                  id="state"
                  title="State"
                  options={loanOptionsConstants.states}
                  value={filters.state}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('state', value)}
                />
                <InputDateRange
                  title="As of Date"
                  value={filters.asOf}
                  onChange={(value) => onChangeFilter('asOf', value)}
                />
              </div>
            </div>

            <div className="border-b border-gray-100 pb-4 mb-4">
              <p className="text-lg font-bold mb-2">Headers</p>
              <div className="flex flex-wrap gap-1">
                <Sortable
                  data={selectedPlaces.map((v, idx) => ({ id: `${v}-${idx}`, data: v }))}
                  setData={(values: any[]) => setSelectedPlaces(values.map(({ data }) => data))}
                  renderItem={(data: any, index: number) => {
                    const splits = data.split('-')
                    if (splits[1] && partyHeaders[splits[1]])
                      data = `${partyTemp[splits[0]] || splits[0]}-${partyHeaders[splits[1]]}`
                    return (
                      <p className="px-2 py-1 border border-gray-200 rounded-md text-sm">
                        {index + 1}. {data}
                      </p>
                    )
                  }}
                />
              </div>
            </div>

            <div className="flex items-center gap-4 justify-between mb-2">
              <p className="text-lg font-bold">Preview ({total})</p>
              <div className="flex items-center gap-4">
                <p className="text-sm cursor-pointer text-shade-blue hover:underline" onClick={onGenerate}>
                  Preview
                </p>
                <span className="p-1 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded" onClick={onDownload}>
                  <Tooltip message="Download Excel">
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </Tooltip>
                </span>
                <span
                  className="p-1 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded"
                  onClick={onDownloadCSV}
                >
                  <Tooltip message="Download CSV">
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </Tooltip>
                </span>
              </div>
            </div>

            {!!preview.length && (
              <HorzScrollContainer size={queryTimes}>
                <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6 overflow-auto">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th className="px-2 py-1">No</th>
                      {preview[0].map((key) => (
                        <th className="px-2 py-1">{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-[14px] text-gray-900">
                    {preview.map((item: string[], index: number) => {
                      if (index == 0) return null
                      return (
                        <tr key={index} className={`border ${index % 2 == 0 ? 'bg-gray-50' : ''}`}>
                          <td className="px-2 py-2">{index}</td>
                          {item.map((v) => (
                            <td className="px-2 py-2 whitespace-nowrap">
                              <span>{v}</span>
                            </td>
                          ))}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </HorzScrollContainer>
            )}
          </div>
        </div>
      </div>
      {isDateModalOpen && (
        <Modal
          title="Date Range Filter"
          isOpen
          titleOkay="Apply"
          titleCancel="Skip"
          onOk={handleDateModalOk}
          onClose={handleDateModalClose}
        >
          <>
            <div className="space-y-4">
              <p className="text-sm text-gray-700">Select which date column should be filtered before continuing.</p>
              <div className="mt-4">
                <RenderInput
                  input={{
                    inputType: 'Select',
                    title: 'Date Column',
                    options: dateModalColumns,
                    value: dateFilterColumn,
                    hasDefaultOption: true,
                  }}
                  Key="date-column-select"
                  onChange={(_key: string, value: string) => {
                    setDateFilterColumn(value)
                    setDateModalError('')
                  }}
                />
              </div>
              {dateFilterColumn && (
                <InputDateRange
                  title="Date Range"
                  name="date-range-filter"
                  value={dateFilterRange}
                  onChange={(value: { from?: string; to?: string }) => {
                    setDateFilterRange(value)
                    setDateModalError('')
                  }}
                />
              )}
              {dateModalError && <p className="text-sm text-rose-600">{dateModalError}</p>}
            </div>
          </>
        </Modal>
      )}
    </div>
  )
}

export default ReportDetails
